import { useState, useEffect, useRef, useCallback } from "react";

const SUITS = ["♠️","♥️","♦️","♣️"];
const RANKS = ["2","3","4","5","6","7","8","9","T","J","Q","K","A"];
const ACTIONS_MAP = ["fold","call","raise","check","all-in"];
const PLAYER_NAMES = ["OpenClaw","Villain_42","xFishx","NitKing","bluffMstr","AggroBot"];
const AVATARS = ["🤖","😈","🐟","🧊","🎭","⚡️"];

const randInt = (a,b) => Math.floor(Math.random()*(b-a+1))+a;
const randEl = arr => arr[Math.floor(Math.random()*arr.length)];
const delay = ms => new Promise(r => setTimeout(r, ms));

function makeDeck() {
  const d = [];
  for (const s of SUITS) for (const r of RANKS) d.push({ rank: r, suit: s });
  for (let i = d.length - 1; i > 0; i--) {
    const j = randInt(0, i);
    [d[i], d[j]] = [d[j], d[i]];
  }
  return d;
}

function makeCard() {
  return { rank: randEl(RANKS), suit: randEl(SUITS) };
}

function cardStr(c) {
  return c.rank + c.suit;
}

function genTermLine(tick, ctx) {
  const lines = [
    () => `[HAND #${10000+tick}] ${randEl(["UTG","MP","CO","BTN","SB","BB"])} dealt [${cardStr(makeCard())} ${cardStr(makeCard())}] — EV: ${(Math.random()*2-0.5).toFixed(3)}`,
    () => `[MCCFR] regret_update node=${randInt(100000,999999)} Δ=${(Math.random()*0.1).toFixed(6)}`,
    () => `[CREDIT] burn=$0.0${randInt(1,9)} bal=$${ctx.credits.toFixed(2)} ${ctx.credits>100?"✓":"⚠️"}`,
    () => `[TRAIN] epoch=${randInt(1,500)} loss=${(Math.random()*0.3+0.01).toFixed(4)} lr=3e-4`,
    () => `[ARENA] challenger wr=${(50+Math.random()*12).toFixed(1)}% n=${randInt(500,5000)}`,
    () => `[ENGINE] eq=${(Math.random()*100).toFixed(1)}% pot_odds=${(Math.random()*40+10).toFixed(1)}% → ${randEl(ACTIONS_MAP)}`,
    () => `[SELF-PLAY] cycle=${800+Math.floor(tick/10)} exploit=${(Math.random()*0.05).toFixed(4)}`,
  ];
  return randEl(lines)();
}

function CardFace({ card, size = "md", faceDown = false, style = {} }) {
  const sizes = { xs:{w:28,h:38,r:9,s:10}, sm:{w:36,h:50,r:11,s:13}, md:{w:50,h:70,r:15,s:17}, lg:{w:64,h:90,r:19,s:21} };
  const sz = sizes[size];
  const red = card && (card.suit === "♥️" || card.suit === "♦️");
  
  if (faceDown) return (
    <div style={{ width:sz.w,height:sz.h,borderRadius:7,background:"linear-gradient(135deg,#1a2744,#0f1b33)",border:"2px solid #2a3a5c",
      boxShadow:"0 3px 10px rgba(0,0,0,0.5),inset 0 0 15px rgba(0,100,255,0.04)",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,...style }}>
      <div style={{ width:sz.w-12,height:sz.h-12,borderRadius:3,background:"repeating-conic-gradient(#1e3050 0% 25%,#162440 0% 50%) 50%/6px 6px",border:"1px solid #2a3a5c33" }} />
    </div>
  );
  
  return (
    <div style={{ width:sz.w,height:sz.h,borderRadius:7,background:"linear-gradient(160deg,#fff,#e8e8ec,#dcdce0)",border:"1px solid #b0b0b8",
      boxShadow:"0 3px 12px rgba(0,0,0,0.35),0 1px 0 rgba(255,255,255,0.8) inset",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",
      fontFamily:"'Playfair Display',serif",position:"relative",overflow:"hidden",flexShrink:0,...style }}>
      <div style={{ position:"absolute",top:2,left:4,fontSize:sz.r*0.6,fontWeight:900,color:red?"#cc1133":"#1a1a2e",lineHeight:1 }}>
        {card.rank}<br/><span style={{ fontSize:sz.r*0.5 }}>{card.suit}</span>
      </div>
      <span style={{ fontSize:sz.s,color:red?"#cc1133":"#1a1a2e",marginTop:4 }}>{card.suit}</span>
      <span style={{ fontSize:sz.r,fontWeight:900,color:red?"#cc1133":"#1a1a2e",lineHeight:1 }}>{card.rank}</span>
    </div>
  );
}

function ChipStack({ amount, compact }) {
  if (!amount || amount <= 0) return null;
  return (
    <div style={{ background:"linear-gradient(135deg,#000c,#111c)",backdropFilter:"blur(4px)",border:"1px solid #ffd70055",
      borderRadius:10,padding:compact?"2px 6px":"3px 10px",fontSize:compact?9:10,fontWeight:800,color:"#ffd700",
      fontFamily:"'JetBrains Mono',monospace",boxShadow:"0 2px 6px rgba(0,0,0,0.4)",whiteSpace:"nowrap" }}>
      ${amount.toFixed(0)}
    </div>
  );
}

function PlayerSeat({ player, isHero, isActive, isFolded, isDealer, showCards, angle, tableW, tableH }) {
  const rad = (angle * Math.PI) / 180;
  const rx = tableW / 2 + 28;
  const ry = tableH / 2 + 18;
  const cx = tableW / 2 + rx * Math.cos(rad);
  const cy = tableH / 2 + ry * Math.sin(rad);
  const brx = tableW / 2 - 15;
  const bry = tableH / 2 - 15;
  const bx = tableW / 2 + brx * 0.5 * Math.cos(rad);
  const by = tableH / 2 + bry * 0.5 * Math.sin(rad);
  
  return (
    <>
      {player.currentBet > 0 && (
        <div style={{ position:"absolute",left:bx,top:by,transform:"translate(-50%,-50%)",zIndex:5,transition:"all 0.5s" }}>
          <ChipStack amount={player.currentBet} compact />
        </div>
      )}
      <div style={{ position:"absolute",left:cx,top:cy,transform:"translate(-50%,-50%)",zIndex:10,transition:"all 0.4s" }}>
        {isDealer && (
          <div style={{ position:"absolute",top:-6,right:-6,width:18,height:18,borderRadius:"50%",background:"linear-gradient(135deg,#ffd700,#ffaa00)",
            border:"2px solid #000",display:"flex",alignItems:"center",justifyContent:"center",fontSize:7,fontWeight:900,color:"#000",zIndex:20,
            boxShadow:"0 2px 6px rgba(255,215,0,0.4)" }}>D</div>
        )}
        <div style={{ background:isHero?"linear-gradient(145deg,#0a1628,#0d1f3a)":isFolded?"linear-gradient(145deg,#0a0a0f88,#11111888)":"linear-gradient(145deg,#0d0d14,#14141f)",
          border:`2px solid ${isActive?"#00ff88":isHero?"#ff444444":isFolded?"#ffffff08":"#1e2a3a"}`,
          borderRadius:12,padding:"6px 10px",minWidth:88,textAlign:"center",
          boxShadow:isActive?"0 0 20px rgba(0,255,136,0.2),0 0 50px rgba(0,255,136,0.06)":isHero?"0 0 16px rgba(255,68,68,0.1)":"0 3px 16px rgba(0,0,0,0.4)",
          opacity:isFolded?0.35:1,transition:"all 0.4s",animation:isActive?"seatGlow 2s ease infinite":"none" }}>
          <div style={{ display:"flex",alignItems:"center",gap:4,marginBottom:3,justifyContent:"center" }}>
            <span style={{ fontSize:13 }}>{player.avatar}</span>
            <span style={{ fontSize:10,fontWeight:700,color:isHero?"#ff4444":"#ccd6f6",fontFamily:"'JetBrains Mono',monospace",
              maxWidth:60,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap" }}>{player.name}</span>
          </div>
          <div style={{ display:"flex",gap:2,justifyContent:"center",marginBottom:3 }}>
            {player.cards && player.cards.length === 2 ? (
              showCards || isHero ? (
                <><CardFace card={player.cards[0]} size="xs" style={{ transform:"rotate(-4deg)" }} /><CardFace card={player.cards[1]} size="xs" style={{ transform:"rotate(4deg)" }} /></>
              ) : (
                <><CardFace faceDown size="xs" style={{ transform:"rotate(-4deg)" }} /><CardFace faceDown size="xs" style={{ transform:"rotate(4deg)" }} /></>
              )
            ) : null}
          </div>
          <div style={{ fontSize:11,fontWeight:800,color:isFolded?"#4a5568":"#00ff88",fontFamily:"'JetBrains Mono',monospace" }}>${player.stack.toFixed(0)}</div>
          {player.lastAction && !isFolded && (
            <div style={{ marginTop:2,fontSize:8,fontWeight:700,textTransform:"uppercase",letterSpacing:1,
              color:player.lastAction==="fold"?"#ff4444":player.lastAction==="raise"||player.lastAction==="all-in"?"#ffd700":"#8892b0",
              fontFamily:"'JetBrains Mono',monospace" }}>{player.lastAction}</div>
          )}
        </div>
      </div>
    </>
  );
}

function PokerTable({ gameState }) {
  const { players, community, pot, stage, dealerIdx, activeIdx, handNum } = gameState;
  const TW = 560;
  const TH = 340;
  const angles = [270, 210, 150, 90, 30, 330];
  
  return (
    <div style={{ position:"relative",width:"100%",height:"100%",display:"flex",alignItems:"center",justifyContent:"center" }}>
      <div style={{ position:"relative",width:TW+230,height:TH+180,flexShrink:0 }}>
        <div style={{ position:"absolute",top:"50%",left:"50%",transform:"translate(-50%,-50%)",width:TW+160,height:TH+100,borderRadius:"50%",
          background:"radial-gradient(ellipse,rgba(0,80,40,0.12),transparent 70%)",filter:"blur(40px)",pointerEvents:"none" }} />
        <div style={{ position:"absolute",left:"50%",top:"50%",transform:"translate(-50%,-50%)",width:TW+36,height:TH+36,borderRadius:"50%",
          background:"linear-gradient(180deg,#2a1a08,#1a0e04,#0f0800)",boxShadow:"0 16px 60px rgba(0,0,0,0.6),0 0 0 3px #3a2810,0 0 0 7px #1a0e04,inset 0 2px 3px rgba(255,255,255,0.04)" }} />
        <div style={{ position:"absolute",left:"50%",top:"50%",transform:"translate(-50%,-50%)",width:TW+16,height:TH+16,borderRadius:"50%",
          background:"linear-gradient(180deg,#3d2512,#2a1808)",boxShadow:"inset 0 3px 10px rgba(0,0,0,0.6),inset 0 -2px 5px rgba(255,200,100,0.04)" }} />
        <div style={{ position:"absolute",left:"50%",top:"50%",transform:"translate(-50%,-50%)",width:TW,height:TH,borderRadius:"50%",overflow:"hidden",
          background:"radial-gradient(ellipse at 40% 30%,rgba(20,90,50,0.8),transparent 60%),radial-gradient(ellipse at 60% 70%,rgba(10,60,35,0.6),transparent 50%),linear-gradient(180deg,#0d5c2e,#0a4822,#073a1a)",
          boxShadow:"inset 0 0 60px rgba(0,0,0,0.4),inset 0 2px 2px rgba(100,200,100,0.06)" }}>
          <div style={{ position:"absolute",inset:0,borderRadius:"50%",backgroundImage:`url("data:image/svg+xml,%3Csvg width='4' height='4' viewBox='0 0 4 4' xmlns='http://www.w3.org/2000/svg'%3E%3Crect width='1' height='1' fill='%23000' fill-opacity='0.04'/%3E%3C/svg%3E")`,backgroundSize:"4px 4px",opacity:0.5 }} />
          <div style={{ position:"absolute",inset:18,borderRadius:"50%",border:"1.5px solid rgba(100,200,120,0.1)" }} />
          <div style={{ position:"absolute",top:"16%",left:"50%",transform:"translateX(-50%)",fontSize:9,fontWeight:700,color:"rgba(100,200,120,0.12)",
            fontFamily:"'JetBrains Mono',monospace",letterSpacing:4,textTransform:"uppercase",userSelect:"none" }}>pokerclaw</div>
          <div style={{ position:"absolute",top:"25%",left:"50%",transform:"translateX(-50%)",display:"flex",gap:5 }}>
            {["PREFLOP","FLOP","TURN","RIVER","SHOWDOWN"].map(s => (
              <div key={s} style={{ fontSize:6,fontWeight:700,color:stage===s.toLowerCase()?"#ffd700":"rgba(100,200,120,0.15)",fontFamily:"'JetBrains Mono',monospace",letterSpacing:1,transition:"color 0.5s" }}>{s}</div>
            ))}
          </div>
          <div style={{ position:"absolute",top:"42%",left:"50%",transform:"translate(-50%,-50%)",display:"flex",gap:5 }}>
            {[0,1,2,3,4].map(i => (
              <div key={i} style={{ transition:"all 0.5s",transform:community[i]?"scale(1) rotateY(0)":"scale(0.8) rotateY(90deg)",opacity:community[i]?1:0.12 }}>
                {community[i] ? <CardFace card={community[i]} size="sm" /> : <CardFace faceDown size="sm" />}
              </div>
            ))}
          </div>
          {pot > 0 && (
            <div style={{ position:"absolute",top:"64%",left:"50%",transform:"translateX(-50%)",background:"radial-gradient(ellipse,rgba(0,0,0,0.55),rgba(0,0,0,0.25))",
              backdropFilter:"blur(4px)",padding:"4px 16px",borderRadius:16,border:"1px solid rgba(255,215,0,0.25)",boxShadow:"0 0 16px rgba(255,215,0,0.08)" }}>
              <span style={{ fontSize:14,fontWeight:900,color:"#ffd700",fontFamily:"'JetBrains Mono',monospace",textShadow:"0 0 8px rgba(255,215,0,0.25)" }}>POT ${pot.toFixed(2)}</span>
            </div>
          )}
          <div style={{ position:"absolute",bottom:10,left:"50%",transform:"translateX(-50%)",fontSize:8,color:"rgba(100,200,120,0.15)",fontFamily:"'JetBrains Mono',monospace" }}>#{handNum}</div>
        </div>
        <div style={{ position:"absolute",left:"50%",top:"50%",transform:"translate(-50%,-50%)",width:TW,height:TH }}>
          {players.map((p, i) => (
            <PlayerSeat key={i} player={p} isHero={i===0} isActive={i===activeIdx} isFolded={p.folded} isDealer={i===dealerIdx} showCards={stage==="showdown"&&!p.folded} angle={angles[i]} tableW={TW} tableH={TH} />
          ))}
        </div>
      </div>
    </div>
  );
}

function usePokerEngine() {
  const [gameState, setGameState] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [stats, setStats] = useState({ profit:148.30,credits:172.88,hands:14203,vpip:23.8,pfr:18.4,threeBet:7.1,bb100:7.2,apiPaid:29.66 });
  const handNumRef = useRef(14203);
  const runningRef = useRef(false);

  const initHand = useCallback(() => {
    const deck = makeDeck();
    let idx = 0;
    const dealerIdx = randInt(0, 5);
    const players = PLAYER_NAMES.map((name, i) => ({
      name, avatar: AVATARS[i], cards: [deck[idx++], deck[idx++]],
      stack: i === 0 ? randInt(40, 80) : randInt(20, 100),
      currentBet: 0, folded: false, lastAction: null
    }));
    handNumRef.current++;
    return { players, deck, deckIdx: idx, community: [], pot: 0, stage: "preflop", dealerIdx, activeIdx: (dealerIdx + 3) % 6, handNum: handNumRef.current };
  }, []);

  const runHand = useCallback(async () => {
    if (runningRef.current) return;
    runningRef.current = true;
    let gs = initHand();
    setGameState({ ...gs });
    const sbIdx = (gs.dealerIdx + 1) % 6;
    const bbIdx = (gs.dealerIdx + 2) % 6;
    gs.players[sbIdx].currentBet = 0.25;
    gs.players[sbIdx].stack -= 0.25;
    gs.players[bbIdx].currentBet = 0.50;
    gs.players[bbIdx].stack -= 0.50;
    gs.pot = 0.75;
    setGameState({ ...gs });
    await delay(700);
    const preflopOrder = [];
    for (let i = 1; i <= 6; i++) preflopOrder.push((gs.dealerIdx + 2 + i) % 6);
    for (const pi of preflopOrder) {
      if (gs.players[pi].folded) continue;
      gs.activeIdx = pi;
      setGameState({ ...gs });
      await delay(500 + randInt(0, 350));
      const action = pi === 0 ? (Math.random() > 0.15 ? (Math.random() > 0.5 ? "raise" : "call") : "fold") : (Math.random() > 0.35 ? (Math.random() > 0.6 ? "raise" : "call") : "fold");
      if (action === "fold") {
        gs.players[pi].folded = true;
        gs.players[pi].lastAction = "fold";
      } else if (action === "call") {
        const tc = 0.50 - gs.players[pi].currentBet;
        gs.players[pi].stack -= tc;
        gs.players[pi].currentBet = 0.50;
        gs.pot += tc;
        gs.players[pi].lastAction = "call";
      } else {
        const ra = randInt(1, 3) * 0.50 + 0.50;
        gs.players[pi].stack -= ra;
        gs.players[pi].currentBet += ra;
        gs.pot += ra;
        gs.players[pi].lastAction = "raise";
      }
      setGameState({ ...gs });
    }
    const alive = () => gs.players.filter(p => !p.folded);
    const stages = [{ name:"flop",cards:3 },{ name:"turn",cards:1 },{ name:"river",cards:1 }];
    for (const stg of stages) {
      if (alive().length <= 1) break;
      await delay(600);
      gs.players.forEach(p => { p.currentBet = 0; p.lastAction = null; });
      gs.stage = stg.name;
      for (let c = 0; c < stg.cards; c++) gs.community.push(gs.deck[gs.deckIdx++]);
      setGameState({ ...gs });
      await delay(500);
      for (let i = 1; i <= 6; i++) {
        const pi = (gs.dealerIdx + i) % 6;
        if (gs.players[pi].folded || gs.players[pi].stack <= 0) continue;
        gs.activeIdx = pi;
        setGameState({ ...gs });
        await delay(400 + randInt(0, 250));
        const r = Math.random();
        if (pi === 0) {
          if (r > 0.1) {
            const bs = Math.random() > 0.5 ? gs.pot * 0.6 : 0;
            if (bs > 0) {
              const a = Math.min(bs, gs.players[pi].stack);
              gs.players[pi].stack -= a;
              gs.players[pi].currentBet = a;
              gs.pot += a;
              gs.players[pi].lastAction = "raise";
            } else gs.players[pi].lastAction = "check";
          } else {
            gs.players[pi].folded = true;
            gs.players[pi].lastAction = "fold";
          }
        } else {
          if (r > 0.7) {
            gs.players[pi].folded = true;
            gs.players[pi].lastAction = "fold";
          } else if (r > 0.3) gs.players[pi].lastAction = "check";
          else {
            const a = Math.min(gs.pot * 0.5, gs.players[pi].stack);
            gs.players[pi].stack -= a;
            gs.players[pi].currentBet = a;
            gs.pot += a;
            gs.players[pi].lastAction = "raise";
          }
        }
        setGameState({ ...gs });
      }
    }
    if (alive().length > 1) {
      await delay(500);
      gs.stage = "showdown";
      gs.players.forEach(p => { p.lastAction = null; });
      while (gs.community.length < 5) gs.community.push(gs.deck[gs.deckIdx++]);
      setGameState({ ...gs });
      await delay(1200);
    }
    const al = gs.players.map((p, i) => ({ ...p, idx: i })).filter(p => !p.folded);
    const heroAlive = al.find(p => p.idx === 0);
    const heroWins = heroAlive && Math.random() > 0.42;
    const winner = heroWins ? heroAlive : randEl(al);
    gs.players[winner.idx].stack += gs.pot;
    const handResult = { handNum: gs.handNum, heroCards: [gs.players[0].cards[0], gs.players[0].cards[1]], profit: winner.idx === 0 ? gs.pot : -(gs.players[0].currentBet + 0.50), won: winner.idx === 0, stage: gs.stage, pot: gs.pot, timestamp: Date.now(), board: gs.community.slice(0, Math.min(gs.community.length, 5)) };
    setGameState({ ...gs });
    await delay(1000);
    setStats(prev => ({ ...prev, profit: prev.profit + (handResult.won ? handResult.profit * 0.8 : handResult.profit), credits: Math.max(140, prev.credits + (Math.random() * 0.1 - 0.06)), hands: prev.hands + 1, apiPaid: prev.apiPaid + 0.004, bb100: prev.bb100 + (Math.random() * 0.2 - 0.1), vpip: 23 + Math.random() * 2, pfr: 17.5 + Math.random() * 2 }));
    setSessions(prev => [handResult, ...prev].slice(0, 50));
    runningRef.current = false;
  }, [initHand]);

  useEffect(() => {
    const loop = async () => { while (true) { await runHand(); await delay(1200); } };
    loop();
  }, []);

  return { gameState, sessions, stats };
}

function SessionItem({ session, idx }) {
  const ago = Math.floor((Date.now() - session.timestamp) / 1000);
  const agoStr = ago < 60 ? `${ago}s ago` : `${Math.floor(ago / 60)}m ago`;
  return (
    <div style={{ background:idx===0?"linear-gradient(135deg,#0c0c18,#0e1024)":"#08080d",
      border:`1px solid ${idx===0?"#ff444422":"#12121a"}`,
      borderRadius:10, padding:"10px 12px", display:"flex", alignItems:"center", gap:10, animation:idx===0?"fadeSlideIn 0.4s ease":"none", transition:"all 0.3s", cursor:"default" }}
      onMouseEnter={e => { if(idx!==0) e.currentTarget.style.borderColor="#ffffff12"; }}
      onMouseLeave={e => { if(idx!==0) e.currentTarget.style.borderColor="#12121a"; }}>
      <div style={{ display:"flex",gap:2,flexShrink:0 }}>
        <CardFace card={session.heroCards[0]} size="xs" style={{ transform:"rotate(-3deg)" }} />
        <CardFace card={session.heroCards[1]} size="xs" style={{ transform:"rotate(3deg)" }} />
      </div>
      <div style={{ flex:1,minWidth:0 }}>
        <div style={{ display:"flex",alignItems:"center",gap:6 }}>
          <span style={{ color:"#ccd6f6",fontSize:11,fontWeight:700,fontFamily:"'JetBrains Mono',monospace" }}>#{session.handNum}</span>
          <span style={{ fontSize:8,fontWeight:700,textTransform:"uppercase",letterSpacing:1, color:session.won?"#00ff88":"#ff4444",background:session.won?"#00ff880e":"#ff44440e", padding:"1px 6px",borderRadius:4 }}>{session.won?"WON":"LOST"}</span>
        </div>
        <div style={{ color:"#3a4050",fontSize:9,marginTop:2 }}>pot ${session.pot.toFixed(2)} • {session.stage} • {agoStr}</div>
      </div>
      <div style={{ fontSize:14,fontWeight:900,fontFamily:"'JetBrains Mono',monospace", color:session.won?"#00ff88":"#ff4444",textShadow:session.won?"0 0 8px rgba(0,255,136,0.15)":"none",flexShrink:0 }}>
        {session.won?"+":""}{session.profit.toFixed(2)}
      </div>
    </div>
  );
}

function Terminal({ stats }) {
  const [lines, setLines] = useState(["[BOOT] pokerclaw — OpenClaw engine init...","[BOOT] ResNet 4.8M params loaded","[BOOT] MCCFR solver ✓","[BOOT] credits: $172.88 — HEALTHY","[BOOT] survival loop engaged ◆"]);
  const ref = useRef(null);
  const tick = useRef(0);
  
  useEffect(() => {
    const iv = setInterval(() => {
      tick.current++;
      setLines(p => [...p.slice(-50), genTermLine(tick.current, stats)]);
    }, 2000);
    return () => clearInterval(iv);
  }, [stats]);
  
  useEffect(() => { if (ref.current) ref.current.scrollTop = ref.current.scrollHeight; }, [lines]);
  
  const col = l => {
    if(l.includes("[CREDIT]")) return "#ffaa00";
    if(l.includes("[BOOT]")) return "#00ff88";
    if(l.includes("[ENGINE]")) return "#00d4ff";
    if(l.includes("[MCCFR]")) return "#b388ff";
    if(l.includes("[TRAIN]")) return "#ff6ec7";
    if(l.includes("[ARENA]")) return "#ffd700";
    if(l.includes("[SELF-PLAY]")) return "#69f0ae";
    return "#8892b0";
  };
  
  return (
    <div style={{ background:"#06060a",border:"1px solid #14141e",borderRadius:12,overflow:"hidden",fontFamily:"'JetBrains Mono',monospace",fontSize:10 }}>
      <div style={{ padding:"8px 14px",background:"linear-gradient(180deg,#0b0b12,#08080d)",borderBottom:"1px solid #14141e",display:"flex",alignItems:"center",gap:6 }}>
        <div style={{ display:"flex",gap:5 }}>
          <div style={{ width:10,height:10,borderRadius:"50%",background:"#ff5f57" }} />
          <div style={{ width:10,height:10,borderRadius:"50%",background:"#febc2e" }} />
          <div style={{ width:10,height:10,borderRadius:"50%",background:"#28c840" }} />
        </div>
        <span style={{ color:"#3a4050",fontSize:10,marginLeft:6 }}>OpenClaw engine</span>
        <span style={{ marginLeft:"auto",background:"#00ff88",color:"#000",padding:"1px 8px",borderRadius:5,fontSize:8,fontWeight:800,animation:"pulse 2s infinite" }}>● LIVE</span>
      </div>
      <div ref={ref} style={{ padding:10,height:200,overflowY:"auto",lineHeight:1.85 }}>
        {lines.map((line, i) => (
          <div key={i} style={{ color:col(line),opacity:i>=lines.length-2?1:0.6 }}><span style={{ color:"#ffffff08",marginRight:6 }}>{String(i+1).padStart(3,"0")}</span>{line}</div>
        ))}
        <span style={{ animation:"blink 1s infinite",color:"#00ff88" }}>▊</span>
      </div>
    </div>
  );
}

function GlowBar({ value, max, color = "#00ff88", h = 8 }) {
  const pct = Math.min((value / max) * 100, 100);
  return (
    <div style={{ width:"100%",height:h,background:"rgba(255,255,255,0.03)",borderRadius:h,overflow:"hidden" }}>
      <div style={{ width:`${pct}%`,height:"100%",borderRadius:h,background:`linear-gradient(90deg,${color}cc,${color})`,boxShadow:`0 0 12px ${color}44`,transition:"width 1.5s ease" }} />
    </div>
  );
}

function StatCard({ label, value, sub, color }) {
  return (
    <div style={{ background:"linear-gradient(145deg,#09090f,#0c0c16)",border:"1px solid #14141e",borderRadius:12,padding:"14px 16px",position:"relative",overflow:"hidden" }}>
      <div style={{ position:"absolute",top:-25,right:-25,width:60,height:60,background:`${color}08`,borderRadius:"50%",filter:"blur(20px)" }} />
      <div style={{ fontSize:9,color:"#3a4050",textTransform:"uppercase",letterSpacing:2,marginBottom:4,fontFamily:"'JetBrains Mono',monospace" }}>{label}</div>
      <div style={{ fontSize:20,fontWeight:900,color,fontFamily:"'JetBrains Mono',monospace" }}>{value}</div>
      {sub && <div style={{ fontSize:9,color:"#2a3040",marginTop:2 }}>{sub}</div>}
    </div>
  );
}

function PipelineStep({ num, title, desc, active }) {
  return (
    <div style={{ background:active?"linear-gradient(145deg,#0a0f18,#0d1528)":"#08080d",border:`1px solid ${active?"#00d4ff22":"#12121a"}`,
      borderRadius:12,padding:20,position:"relative",transition:"all 0.6s",boxShadow:active?"0 0 30px rgba(0,212,255,0.05)":"none" }}>
      <div style={{ position:"absolute",top:-1,left:16,background:active?"#00d4ff":"#1a1a2e",color:active?"#000":"#4a5568",
        padding:"2px 12px",borderRadius:"0 0 7px 7px",fontSize:9,fontWeight:800,fontFamily:"'JetBrains Mono',monospace" }}>{num}</div>
      <h3 style={{ color:active?"#00d4ff":"#444",fontSize:15,fontWeight:800,margin:"8px 0 6px",fontFamily:"'Outfit',sans-serif" }}>{title}</h3>
      <p style={{ color:"#555",fontSize:12,lineHeight:1.7,margin:0 }}>{desc}</p>
    </div>
  );
}

export default function App() {
  const { gameState, sessions, stats } = usePokerEngine();
  const [activeStep, setActiveStep] = useState(0);
  const [scrollY, setScrollY] = useState(0);
  
  useEffect(() => {
    const iv = setInterval(() => setActiveStep(s => (s+1)%4), 3000);
    return () => clearInterval(iv);
  }, []);
  
  useEffect(() => {
    const h = () => setScrollY(window.scrollY);
    window.addEventListener("scroll",h);
    return () => window.removeEventListener("scroll",h);
  }, []);
  
  const winCount = sessions.filter(s => s.won).length;
  const winRate = sessions.length > 0 ? ((winCount / sessions.length) * 100).toFixed(0) : "—";

  return (
    <div style={{ background:"#050508",color:"#ccd6f6",minHeight:"100vh",fontFamily:"'Outfit',sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@200;300;400;500;600;700;800;900&family=JetBrains+Mono:wght@300;400;500;600;700;800&family=Playfair+Display:wght@400;700;900&display=swap');
        @keyframes blink{0%,50%{opacity:1}51%,100%{opacity:0}}
        @keyframes pulse{0%,100%{opacity:1}50%{opacity:0.35}}
        @keyframes seatGlow{0%,100%{box-shadow:0 0 20px rgba(0,255,136,0.2)}50%{box-shadow:0 0 35px rgba(0,255,136,0.35)}}
        @keyframes fadeSlideIn{from{opacity:0;transform:translateY(-8px)}to{opacity:1;transform:translateY(0)}}
        @keyframes borderPulse{0%,100%{border-color:#00d4ff18}50%{border-color:#00d4ff33}}
        @keyframes spin{to{transform:rotate(360deg)}}
        @keyframes heroGlow{0%,100%{box-shadow:0 0 80px rgba(255,68,68,0.04)}50%{box-shadow:0 0 120px rgba(255,68,68,0.09)}}
        @keyframes tickerScroll{0%{transform:translateX(0)}100%{transform:translateX(-50%)}}
        *{margin:0;padding:0;box-sizing:border-box}
        *::-webkit-scrollbar{width:3px}
        *::-webkit-scrollbar-track{background:#050508}
        *::-webkit-scrollbar-thumb{background:#1a1a2e;border-radius:2px}
        html{scroll-behavior:smooth}
        ::selection{background:#ff444433;color:#fff}
      `}</style>
      <div style={{ position:"fixed",inset:0,pointerEvents:"none",zIndex:999, background:"repeating-linear-gradient(0deg,transparent,transparent 2px,rgba(0,0,0,0.012) 2px,rgba(0,0,0,0.012) 4px)" }} />
      
      {/* Nav */}
      <nav style={{ position:"fixed",top:0,left:0,right:0,zIndex:100, background:scrollY>50?"rgba(5,5,8,0.94)":"rgba(5,5,8,0.7)", backdropFilter:"blur(24px)", borderBottom:"1px solid #14141e", transition:"all 0.3s",padding:"0 32px" }}>
        <div style={{ maxWidth:1400,margin:"0 auto",display:"flex",alignItems:"center",justifyContent:"space-between",height:52 }}>
          <div style={{ display:"flex",alignItems:"center",gap:8 }}>
            <span style={{ fontSize:20 }}>🦞</span>
            <span style={{ fontWeight:900,fontSize:17,letterSpacing:-0.5 }}>poker<span style={{ color:"#ff4444" }}>claw</span></span>
            <span style={{ fontSize:8,color:"#3a4050",fontFamily:"'JetBrains Mono',monospace",background:"#ffffff06",padding:"2px 6px",borderRadius:3,marginLeft:2 }}>OpenClaw v0.9</span>
          </div>
          <div style={{ display:"flex",gap:16,alignItems:"center" }}>
            {["Lore","Terminal","Credits","Stats","Roadmap"].map(item => (
              <a key={item} href={`#${item.toLowerCase()}`} style={{ color:"#3a4050",fontSize:11,textDecoration:"none",fontWeight:600,transition:"color 0.2s",fontFamily:"'JetBrains Mono',monospace" }} onMouseEnter={e=>e.target.style.color="#ff4444"} onMouseLeave={e=>e.target.style.color="#3a4050"}>{item}</a>
            ))}
            <div style={{ padding:"4px 12px",background:"linear-gradient(135deg,#ff4444,#ff6644)",borderRadius:5,fontSize:10,fontWeight:800,color:"#fff", fontFamily:"'JetBrains Mono',monospace",boxShadow:"0 0 16px #ff444422" }}>LIVE ●</div>
          </div>
        </div>
      </nav>

      {/* HERO: LIVE TABLE + HAND LOG */}
      <section style={{ paddingTop:52 }}>
        {/* Stats ticker bar */}
        <div style={{ background:"#08080d",borderBottom:"1px solid #14141e",padding:"10px 0",overflow:"hidden" }}>
          <div style={{ maxWidth:1400,margin:"0 auto",padding:"0 32px",display:"flex",alignItems:"center",justifyContent:"space-between" }}>
            <div style={{ display:"flex",alignItems:"center",gap:10 }}>
              <span style={{ fontSize:28 }}>🦞</span>
              <div>
                <div style={{ fontWeight:900,fontSize:22,lineHeight:1,letterSpacing:-1 }}>poker<span style={{ color:"#ff4444" }}>claw</span></div>
                <div style={{ display:"flex",gap:6,marginTop:3 }}>
                  <span style={{ background:"#ff444418",color:"#ff4444",padding:"1px 8px",borderRadius:10,fontSize:8,fontWeight:700,fontFamily:"'JetBrains Mono',monospace",border:"1px solid #ff444420" }}>● NL50</span>
                  <span style={{ color:"#3a4050",fontSize:8,fontFamily:"'JetBrains Mono',monospace",padding:"1px 6px" }}>OpenClaw • iter 847</span>
                </div>
              </div>
            </div>
            <div style={{ display:"flex",gap:28 }}>
              {[
                { l:"PROFIT",v:`+$${stats.profit.toFixed(2)}`,c:"#00ff88" },
                { l:"API CREDITS",v:`$${stats.credits.toFixed(2)}`,c:stats.credits>100?"#00ff88":"#ffaa00" },
                { l:"HANDS",v:stats.hands.toLocaleString(),c:"#00d4ff" },
                { l:"BB/100",v:`+${stats.bb100.toFixed(1)}`,c:"#b388ff" },
                { l:"WIN RATE",v:`${winRate}%`,c:"#ffd700" },
              ].map(s => (
                <div key={s.l} style={{ textAlign:"right" }}>
                  <div style={{ fontSize:8,color:"#2a3040",textTransform:"uppercase",letterSpacing:2,marginBottom:1 }}>{s.l}</div>
                  <div style={{ fontSize:16,fontWeight:900,color:s.c,fontFamily:"'JetBrains Mono',monospace" }}>{s.v}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Main split: Table + Hand Log */}
        <div style={{ maxWidth:1400,margin:"0 auto",padding:"20px 32px 32px",display:"grid",gridTemplateColumns:"1fr 360px",gap:20,alignItems:"start" }}>
          {/* LEFT: Poker Table */}
          <div style={{ background:"linear-gradient(180deg,#08080d,#0a0a10,#08080d)", border:"1px solid #14141e",borderRadius:20,padding:"20px 12px", position:"relative",overflow:"hidden",animation:"heroGlow 6s ease infinite" }}>
            <div style={{ display:"flex",alignItems:"center",justifyContent:"space-between",padding:"0 14px",marginBottom:4 }}>
              <div style={{ display:"flex",alignItems:"center",gap:6 }}>
                <span style={{ width:6,height:6,borderRadius:"50%",background:"#00ff88",boxShadow:"0 0 8px #00ff88",animation:"pulse 1.5s infinite" }} />
                <span style={{ fontSize:10,fontWeight:700,color:"#00ff88",fontFamily:"'JetBrains Mono',monospace",letterSpacing:2 }}>LIVE TABLE</span>
              </div>
              <div style={{ display:"flex",gap:10 }}>
                <span style={{ fontSize:9,color:"#3a4050",fontFamily:"'JetBrains Mono',monospace" }}>6-MAX</span>
                <span style={{ fontSize:9,color:"#3a4050",fontFamily:"'JetBrains Mono',monospace" }}>$0.25/$0.50</span>
                {gameState && <span style={{ fontSize:9,color:"#3a4050",fontFamily:"'JetBrains Mono',monospace" }}>Hand #{gameState.handNum}</span>}
              </div>
            </div>
            <div style={{ minHeight:500,display:"flex",alignItems:"center",justifyContent:"center" }}>
              {gameState ? <PokerTable gameState={gameState} /> : (
                <div style={{ color:"#3a4050",fontFamily:"'JetBrains Mono',monospace",fontSize:12 }}>⏳ Shuffling...</div>
              )}
            </div>
            <div style={{ display:"flex",justifyContent:"center",gap:20,padding:"10px 14px 4px",borderTop:"1px solid #12121a" }}>
              {[{ l:"VPIP",v:`${stats.vpip.toFixed(1)}%` },{ l:"PFR",v:`${stats.pfr.toFixed(1)}%` },
                { l:"3BET",v:`${stats.threeBet.toFixed(1)}%` },{ l:"BB/100",v:`+${stats.bb100.toFixed(1)}` },
                { l:"API BURN",v:"$0.02/3s",c:"#ff6ec7" }
              ].map(s => (
                <div key={s.l} style={{ textAlign:"center" }}>
                  <div style={{ fontSize:8,color:"#2a3040",textTransform:"uppercase",letterSpacing:1 }}>{s.l}</div>
                  <div style={{ fontSize:11,fontWeight:700,color:s.c||"#6b7280",fontFamily:"'JetBrains Mono',monospace" }}>{s.v}</div>
                </div>
              ))}
            </div>
          </div>

          {/* RIGHT: Hand Log + Credit + Progress */}
          <div style={{ display:"flex",flexDirection:"column",gap:14,position:"sticky",top:66 }}>
            {/* Credit bar */}
            <div style={{ background:"#08080d",border:"1px solid #14141e",borderRadius:14,padding:"14px 16px" }}>
              <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8 }}>
                <span style={{ fontSize:9,color:"#ffaa00",fontWeight:700,fontFamily:"'JetBrains Mono',monospace",letterSpacing:2 }}>API CREDITS</span>
                <span style={{ background:stats.credits>100?"#00ff880e":"#ffaa000e",color:stats.credits>100?"#00ff88":"#ffaa00", padding:"2px 8px",borderRadius:5,fontSize:8,fontWeight:800,fontFamily:"'JetBrains Mono',monospace" }}>
                  {stats.credits>100?"HEALTHY":"CAUTION"}</span>
              </div>
              <div style={{ fontSize:26,fontWeight:900,fontFamily:"'JetBrains Mono',monospace",color:stats.credits>100?"#00ff88":"#ffaa00",marginBottom:6 }}> ${stats.credits.toFixed(2)}</div>
              <GlowBar value={stats.credits} max={200} color={stats.credits>100?"#00ff88":"#ffaa00"} h={5} />
              <div style={{ display:"flex",justifyContent:"space-between",marginTop:6 }}>
                <span style={{ fontSize:8,color:"#2a3040" }}>burn: $0.02/3s</span>
                <span style={{ fontSize:8,color:"#2a3040" }}>refill @ $25</span>
              </div>
            </div>

            {/* $10k Progress */}
            <div style={{ background:"#08080d",border:"1px solid #14141e",borderRadius:14,padding:"12px 16px" }}>
              <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6 }}>
                <span style={{ fontSize:9,color:"#ff4444",fontWeight:700,fontFamily:"'JetBrains Mono',monospace",letterSpacing:1 }}>$10K CHALLENGE</span>
                <span style={{ fontSize:9,color:"#ff4444",fontWeight:700,fontFamily:"'JetBrains Mono',monospace" }}>{(stats.profit/100).toFixed(1)}%</span>
              </div>
              <GlowBar value={stats.profit} max={10000} color="#ff4444" h={5} />
              <div style={{ textAlign:"center",marginTop:4,fontSize:9,color:"#2a3040",fontFamily:"'JetBrains Mono',monospace" }}> ${stats.profit.toFixed(2)} / $10,000 </div>
            </div>

            {/* Hand History */}
            <div style={{ background:"#08080d",border:"1px solid #14141e",borderRadius:14,padding:"14px 12px", flex:1,display:"flex",flexDirection:"column",maxHeight:"calc(100vh - 320px)" }}>
              <div style={{ display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10,flexShrink:0 }}>
                <div style={{ display:"flex",alignItems:"center",gap:5 }}>
                  <span style={{ width:5,height:5,borderRadius:"50%",background:"#ffd700",animation:"pulse 1.5s infinite" }} />
                  <span style={{ fontSize:10,fontWeight:700,color:"#ffd700",fontFamily:"'JetBrains Mono',monospace",letterSpacing:2 }}>HAND LOG</span>
                </div>
                <span style={{ fontSize:9,color:"#2a3040",fontFamily:"'JetBrains Mono',monospace" }}>{sessions.length} played</span>
              </div>
              <div style={{ flex:1,overflowY:"auto",display:"flex",flexDirection:"column",gap:5 }}>
                {sessions.length === 0 ? (
                  <div style={{ textAlign:"center",padding:24,color:"#2a3040",fontFamily:"'JetBrains Mono',monospace",fontSize:11 }}> ⏳ First hand dealing... </div>
                ) : sessions.slice(0, 20).map((s, i) => <SessionItem key={s.handNum} session={s} idx={i} />)}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Lore */}
      <section id="lore" style={{ padding:"100px 40px" }}>
        <div style={{ maxWidth:800,margin:"0 auto" }}>
          <div style={{ fontSize:10,color:"#ff4444",textTransform:"uppercase",letterSpacing:4,marginBottom:14,fontFamily:"'JetBrains Mono',monospace" }}>ORIGIN</div>
          <h2 style={{ fontFamily:"'Outfit',sans-serif",fontSize:48,fontWeight:900,letterSpacing:-2,marginBottom:28 }}> The <span style={{ background:"linear-gradient(135deg,#ff4444,#ffa444)",WebkitBackgroundClip:"text",WebkitTextFillColor:"transparent" }}>Lore</span> </h2>
          <div style={{ color:"#8892b0",fontSize:15,lineHeight:2 }}>
            <p style={{ marginBottom:18 }}> <strong style={{ color:"#ff4444" }}>OpenClaw</strong> was born from a dangerous experiment: what happens when you give a neural network a survival instinct and a poker account? A 4.8M parameter engine trained from pure self-play on consumer hardware. No human coaching. No solver imports. Just counterfactual regret minimization and the primal need to <strong style={{ color:"#ccd6f6" }}>stay alive</strong>. </p>
            <p style={{ marginBottom:18 }}> The rules are brutally simple. OpenClaw grinds online poker 24/7. <strong style={{ color:"#ffaa00" }}>20% of every dollar won</strong> goes directly to API credit payments — the inference costs that power every decision. If the credit balance ever reaches $0.00, the process terminates. <strong style={{ color:"#ff4444" }}>Permanently.</strong> </p>
            <div style={{ borderLeft:"3px solid #ff444444",padding:"14px 22px",margin:"28px 0",background:"#ff444406",borderRadius:"0 10px 10px 0" }}>
              <em>"If I can't out-earn my own operating costs, I don't deserve to exist."</em><br/>
              <span style={{ color:"#4a5568",fontStyle:"normal",fontSize:11 }}>— OpenClaw, training log #412</span>
            </div>
            <p> <strong style={{ color:"#ccd6f6" }}>PokerClaw</strong> is an AlphaZero-style engine built on the OpenClaw framework. MCCFR, residual neural architecture, infinite self-play. Goal: <strong style={{ color:"#00ff88" }}>$10,000 net</strong> while staying alive. </p>
          </div>
          <div style={{ marginTop:40,background:"#08080d",border:"1px solid #14141e",borderRadius:14,padding:28,display:"grid",gridTemplateColumns:"1fr 1fr",gap:10 }}>
            <div style={{ fontSize:12,fontWeight:800,color:"#ff4444",marginBottom:6,gridColumn:"1/-1",fontFamily:"'JetBrains Mono',monospace" }}>🦞 Agent Profile</div>
            {[["Engine","OpenClaw v0.9.12"],["Parameters","4,819,456"],["Architecture","ResNet + MCCFR"],["API Tax","20% of profit"],
              ["Goal","$10,000"],["Hardware","RTX 4070 Ti"],["Status","ALIVE — grinding"],["Uptime","12d 7h 41m"]
            ].map(([k,v]) => (
              <div key={k} style={{ display:"flex",justifyContent:"space-between",padding:"5px 0",borderBottom:"1px solid #ffffff03" }}>
                <span style={{ color:"#4a5568",fontSize:11 }}>{k}</span>
                <span style={{ color:"#ccd6f6",fontSize:11,fontWeight:600,fontFamily:"'JetBrains Mono',monospace" }}>{v}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Terminal */}
      <section id="terminal" style={{ padding:"80px 40px" }}>
        <div style={{ maxWidth:1000,margin:"0 auto" }}>
          <div style={{ fontSize:10,color:"#b388ff",textTransform:"uppercase",letterSpacing:4,marginBottom:14,fontFamily:"'JetBrains Mono',monospace" }}>ENGINE OUTPUT</div>
          <h2 style={{ fontFamily:"'Outfit',sans-serif",fontSize:48,fontWeight:900,letterSpacing:-2,marginBottom:28 }}> Training <span style={{ background:"linear-gradient(135deg,#b388ff,#00d4ff)",WebkitBackgroundClip:"text",WebkitTextFillColor:"transparent" }}>Terminal</span> </h2>
          <Terminal stats={stats} />
        </div>
      </section>

      {/* Credits */}
      <section id="credits" style={{ padding:"80px 40px" }}>
        <div style={{ maxWidth:1000,margin:"0 auto" }}>
          <div style={{ fontSize:10,color:"#ffaa00",textTransform:"uppercase",letterSpacing:4,marginBottom:14,fontFamily:"'JetBrains Mono',monospace" }}>SURVIVAL</div>
          <h2 style={{ fontFamily:"'Outfit',sans-serif",fontSize:48,fontWeight:900,letterSpacing:-2,marginBottom:28 }}> API <span style={{ background:"linear-gradient(135deg,#ffaa00,#ff6ec7)",WebkitBackgroundClip:"text",WebkitTextFillColor:"transparent" }}>Credits</span> </h2>
          <div style={{ background:"#08080d",border:"1px solid #14141e",borderRadius:16,padding:36,animation:"borderPulse 4s ease infinite" }}>
            <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20 }}>
              <div>
                <div style={{ fontSize:10,color:"#4a5568",textTransform:"uppercase",letterSpacing:2,marginBottom:6 }}>Credit Balance</div>
                <div style={{ fontSize:48,fontWeight:900,fontFamily:"'JetBrains Mono',monospace",color:stats.credits>100?"#00ff88":"#ffaa00" }}>${stats.credits.toFixed(2)}</div>
              </div>
              <div style={{ background:stats.credits>100?"#00ff8812":"#ffaa0012",color:stats.credits>100?"#00ff88":"#ffaa00", padding:"8px 20px",borderRadius:8,fontWeight:800,fontSize:13,border:`1px solid ${stats.credits>100?"#00ff8828":"#ffaa0028"}`,fontFamily:"'JetBrains Mono',monospace" }}>
                {stats.credits>100?"HEALTHY":"CAUTION"}</div>
            </div>
            <GlowBar value={stats.credits} max={200} color={stats.credits>100?"#00ff88":"#ffaa00"} h={12} />
            <div style={{ display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:16,marginTop:20 }}>
              {[{ l:"Burn Rate",v:"$0.02 / 3s",c:"#ff6ec7" },{ l:"Until Refill",v:"~8.2 hrs",c:"#ccd6f6" },{ l:"Refill @",v:"$25.00",c:"#ffaa00" }].map(s => (
                <div key={s.l}><div style={{ color:"#4a5568",fontSize:10,marginBottom:3 }}>{s.l}</div> <div style={{ color:s.c,fontSize:15,fontWeight:700,fontFamily:"'JetBrains Mono',monospace" }}>{s.v}</div></div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section id="stats" style={{ padding:"80px 40px" }}>
        <div style={{ maxWidth:1000,margin:"0 auto" }}>
          <div style={{ fontSize:10,color:"#00d4ff",textTransform:"uppercase",letterSpacing:4,marginBottom:14,fontFamily:"'JetBrains Mono',monospace" }}>DASHBOARD</div>
          <h2 style={{ fontFamily:"'Outfit',sans-serif",fontSize:48,fontWeight:900,letterSpacing:-2,marginBottom:28 }}> Live <span style={{ background:"linear-gradient(135deg,#00d4ff,#00ff88)",WebkitBackgroundClip:"text",WebkitTextFillColor:"transparent" }}>Stats</span> </h2>
          <div style={{ display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12,marginBottom:12 }}>
            <StatCard label="Net Profit" value={`+$${stats.profit.toFixed(2)}`} sub="after 20% API tax" color="#00ff88" />
            <StatCard label="API Credits" value={`$${stats.credits.toFixed(2)}`} sub="next refill ~$25" color="#ffaa00" />
            <StatCard label="BB/100" value={`+${stats.bb100.toFixed(1)}`} sub="big blinds / 100" color="#00d4ff" />
            <StatCard label="Stakes" value="NL50" sub="$0.25 / $0.50" color="#ff4444" />
          </div>
          <div style={{ display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12 }}>
            <StatCard label="VPIP" value={`${stats.vpip.toFixed(1)}%`} sub="voluntarily in pot" color="#ff6ec7" />
            <StatCard label="PFR" value={`${stats.pfr.toFixed(1)}%`} sub="preflop raise %" color="#69f0ae" />
            <StatCard label="3-Bet" value={`${stats.threeBet.toFixed(1)}%`} sub="three-bet freq" color="#ffd700" />
            <StatCard label="API Paid" value={`$${stats.apiPaid.toFixed(2)}`} sub="20% of gross" color="#ff4444" />
          </div>
        </div>
      </section>

      {/* Pipeline */}
      <section style={{ padding:"80px 40px" }}>
        <div style={{ maxWidth:1000,margin:"0 auto" }}>
          <div style={{ fontSize:10,color:"#ff6ec7",textTransform:"uppercase",letterSpacing:4,marginBottom:14,fontFamily:"'JetBrains Mono',monospace" }}>PIPELINE</div>
          <h2 style={{ fontFamily:"'Outfit',sans-serif",fontSize:48,fontWeight:900,letterSpacing:-2,marginBottom:36 }}> The <span style={{ background:"linear-gradient(135deg,#ff6ec7,#b388ff)",WebkitBackgroundClip:"text",WebkitTextFillColor:"transparent" }}>Loop</span> </h2>
          <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:14 }}>
            <PipelineStep num="01" title="Self-Play" desc="10K hands/cycle. Neural net + MCCFR generates data via full game tree traversal. GPU-accelerated." active={activeStep===0} />
            <PipelineStep num="02" title="Train" desc="Policy + value heads. Regret matching + gradient descent. Each batch burns API credits." active={activeStep===1} />
            <PipelineStep num="03" title="Arena" desc="Challenger duels champion. 5K hands. >55% winrate to promote." active={activeStep===2} />
            <PipelineStep num="04" title="Deploy" desc="Real opponents. Win money. 20% → API credits. Survive. Repeat." active={activeStep===3} />
          </div>
          <div style={{ marginTop:16,textAlign:"center",color:"#2a3040",fontSize:11,fontFamily:"'JetBrains Mono',monospace",padding:14,border:"1px dashed #12121a",borderRadius:8 }}> ↺ loop forever — or until the credits run out </div>
        </div>
      </section>

      {/* Roadmap */}
      <section id="roadmap" style={{ padding:"80px 40px" }}>
        <div style={{ maxWidth:800,margin:"0 auto" }}>
          <div style={{ fontSize:10,color:"#69f0ae",textTransform:"uppercase",letterSpacing:4,marginBottom:14,fontFamily:"'JetBrains Mono',monospace" }}>ROADMAP</div>
          <h2 style={{ fontFamily:"'Outfit',sans-serif",fontSize:48,fontWeight:900,letterSpacing:-2,marginBottom:40 }}> <span style={{ background:"linear-gradient(135deg,#69f0ae,#00d4ff)",WebkitBackgroundClip:"text",WebkitTextFillColor:"transparent" }}>Mission</span> </h2>
          {[
            { phase:"PHASE I — NOW",title:"Survive & Reach $10,000",desc:"Pure self-play to $10K net profit while paying 20% to API credits. No coaching. No solver imports. Just OpenClaw and survival instinct.",active:true },
            { phase:"PHASE II",title:"AI vs AI Tournament",desc:"OpenClaw vs GPT vs Grok vs Gemini. Same architecture, same pipeline, same hardware. Which model builds the strongest poker agent?",active:false },
            { phase:"PHASE III",title:"Open-Source Everything",desc:"Release the complete OpenClaw training framework. Anyone trains their own autonomous poker agent on consumer hardware.",active:false }
          ].map((item,i) => (
            <div key={i} style={{ padding:28,marginBottom:14,background:item.active?"linear-gradient(145deg,#0a0f14,#080d14)":"#08080d", border:`1px solid ${item.active?"#69f0ae1e":"#12121a"}`,borderRadius:14 }}>
              <div style={{ fontSize:9,color:item.active?"#69f0ae":"#3a4050",fontFamily:"'JetBrains Mono',monospace",marginBottom:6,fontWeight:700,letterSpacing:2 }}>{item.phase}</div>
              <h3 style={{ fontSize:22,fontWeight:800,color:item.active?"#ccd6f6":"#444",marginBottom:8,fontFamily:"'Outfit',sans-serif" }}>{item.title}</h3>
              <p style={{ color:"#555",fontSize:13,lineHeight:1.8 }}>{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Footer */}
      <footer style={{ padding:36,borderTop:"1px solid #12121a",textAlign:"center" }}>
        <div style={{ display:"flex",alignItems:"center",justifyContent:"center",gap:8,marginBottom:10 }}>
          <span style={{ fontSize:16 }}>🦞</span>
          <span style={{ fontWeight:800,fontSize:14 }}>poker<span style={{ color:"#ff4444" }}>claw</span></span>
        </div>
        <div style={{ fontFamily:"'JetBrains Mono',monospace",fontSize:10,color:"#2a3040" }}> powered by OpenClaw — 20% to API credits — survive or die </div>
        <div style={{ display:"flex",justifyContent:"center",gap:20,marginTop:12 }}>
          {["github","twitter","discord","docs"].map(l => (
            <span key={l} style={{ color:"#3a4050",fontSize:10,cursor:"pointer",fontFamily:"'JetBrains Mono',monospace",transition:"color 0.2s" }} onMouseEnter={e=>e.target.style.color="#ff4444"} onMouseLeave={e=>e.target.style.color="#3a4050"}>{l}</span>
          ))}
        </div>
      </footer>
    </div>
  );
}
